/**
 * @file product-suppliers.tsx
 * @module app/modules/products/product-details-tabs/product-suppliers
 * @description "Supplier details" row of the product Basic details: vendors assigned to the
 *   product (with lead time, PO tonnage and this product's MOQ) and the last completed PO in the
 *   selected warehouse.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { PURCHASEORDER } from "../../../../api/apiEndPoints";
import Method from "../../../../utils/methods";
import { useWarehouse } from "../../../context/WarehouseContext";
import { ProductSupplier, ProductSuppliersResponse } from "../../uProcure/po-management/draft-po.types";

const UNIT: Record<number, string> = { 1: "CWD", 2: "DWP", 3: "P", 4: "CWP" };

const ProductSuppliers = ({ variantId }: { variantId?: string }) => {
  const { selectedWarehouse } = useWarehouse();
  const [data, setData] = useState<ProductSuppliersResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!variantId) return;
    (async () => {
      setLoading(true);
      const api = new APICallService(
        PURCHASEORDER.productSuppliers,
        { warehouse: selectedWarehouse?.value ?? "" },
        { variant: variantId },
        "",
        "",
        "",
        "",
        selectedWarehouse?.value
      );
      const res = await api.callAPI();
      setData(res && Array.isArray(res.assigned) ? res : null);
      setLoading(false);
    })();
  }, [variantId, selectedWarehouse?.value]);

  const supplierLine = (s: ProductSupplier) => (
    <>
      <span className="fw-bolder">{s.name || "-"}</span>
      {s.contact && <span className="text-gray-700"> · {s.contact}</span>}
      {s.phone && <span className="text-gray-700"> · {s.phone}</span>}
      <div className="fs-14 text-gray-700">
        Lead time: {s.leadDurationDays ? `${s.leadDurationDays} days` : "not set"}
        {" · "}PO tonnage: {s.tonnageTon ? `${s.tonnageTon} t` : "not set"}
        {" · "}MOQ: {s.moq ? `${s.moq.quantity} ${UNIT[s.moq.quantityType] ?? ""}` : "not set"}
      </div>
    </>
  );

  return (
    <Row className="mb-7">
      <Col lg={2}>
        <label className="fs-16 fw-500 text-dark">Supplier details:</label>
      </Col>
      <Col lg={10} className="fs-16 fw-600 text-dark">
        {loading ? (
          <Spinner animation="border" size="sm" />
        ) : !data ? (
          <span className="text-gray-600">Not available</span>
        ) : (
          <>
            {data.lastPo ? (
              <div className="mb-3">
                <div className="fs-14 text-gray-600 fw-500">
                  Last completed PO: {data.lastPo.refKey} ({Method.convertDateToFormat(data.lastPo.date, "DD/MM/YYYY")}
                  {data.lastPo.warehouse ? `, ${data.lastPo.warehouse}` : ""}) · {data.lastPo.quantity}{" "}
                  {data.lastPo.quantityType !== null ? UNIT[data.lastPo.quantityType] ?? "" : ""}
                  {data.lastPo.purchaseAmount ? ` at TSh ${Number(data.lastPo.purchaseAmount).toLocaleString("en-US")}` : ""}
                </div>
                {supplierLine(data.lastPo.supplier)}
              </div>
            ) : (
              <div className="fs-14 text-gray-600 fw-500 mb-3">No completed PO in this warehouse yet</div>
            )}
            {data.assigned.length > 0 && (
              <div>
                <div className="fs-14 text-gray-600 fw-500">
                  Assigned suppliers (Assign Products to Vendors): {data.assigned.length}
                </div>
                {data.assigned.map((s) => (
                  <div key={s._id} className="mb-2">
                    {supplierLine(s)}
                  </div>
                ))}
              </div>
            )}
            {!data.lastPo && !data.assigned.length && <span className="text-gray-600">No supplier assigned</span>}
          </>
        )}
      </Col>
    </Row>
  );
};

export default ProductSuppliers;
